import { colors } from '@fikasio/styles';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sidebar as ProSidebar, Menu, MenuItem, SubMenu, sidebarClasses } from 'react-pro-sidebar';
import AdminIcon from 'components/admin/AdminIcon';
import { useAdminSections } from 'components/admin/sections';
import { MAIN_SIDEBAR_WIDTH, TOP_BAR_HEIGHT } from 'components/UI/layout';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { ClickOutside, Dot, Icon } from '@fikasio/react-ui-components';
import NotificationsCounter from 'components/notifications/NotificationsCounter';
import { calculateNotifications } from 'components/notifications/utils';
import { getLateProjects } from 'components/projects/utils';
import UserAvatar from 'components/UI/UserAvatar';
import { useGetOrganizationsQuery } from 'services/organizations/api';
import { useGetProjectsQuery } from 'services/projects/api';
import { useGetTasksQuery } from 'services/tasks/api';
import { FEATURES } from 'services/features/catalog';
import { useFeatureEnabled } from 'services/features/hooks';
import { useAuth } from 'services/login/hooks';
import links, { linkKey } from 'utils/links';
import './Sidebar.css';

const Sidebar = () => {
  const { data: organizations } = useGetOrganizationsQuery();
  const currentOrganization = organizations?.[0];
  const { data: projects } = useGetProjectsQuery();
  const { enabled: objectivesEnabled } = useFeatureEnabled(FEATURES.objectives);
  const { enabled: tasksEnabled } = useFeatureEnabled(FEATURES.tasks);
  const { enabled: timesheetEnabled } = useFeatureEnabled(FEATURES.timesheet);
  const { data } = useGetTasksQuery({}, { skip: !tasksEnabled });
  const tasks = tasksEnabled ? data?.tasks : undefined;
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const adminSections = useAdminSections();
  const adminHome = adminSections[0]?.path;
  const isSection = (path: string) => (
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  );
  const isAdminRoute = isSection(links.paths.admin);

  const notifications = tasks && projects && calculateNotifications(tasks, projects);
  const isOverloadedInTheFuture = !!notifications?.overloadInTheFuture;
  const lateProjects = tasksEnabled
    ? notifications?.lateProjects
    : (projects ? getLateProjects(projects, []) : undefined);
  const lateTasks = notifications?.lateTasks;
  const tasksDueAfterProjectDue = notifications?.tasksDueAfterProjectDue;
  const notificationsCount = tasksEnabled
    ? (isOverloadedInTheFuture ? 1 : 0)
      + (lateTasks && lateTasks.length)
      + (lateProjects && lateProjects.length)
      + (tasksDueAfterProjectDue && tasksDueAfterProjectDue.length)
    : (lateProjects?.length || 0);

  return (
    <ClickOutside
      onClickOutside={() => {
        setIsExpanded(false);
      }}
    >
      {
        !isExpanded && (
          <>
            <Tooltip
              id="home"
              style={{
                zIndex: 1000,
              }}
            />
            <Tooltip
              id="projects"
              style={{
                zIndex: 1000,
              }}
            />
            {
              objectivesEnabled && (
                <Tooltip
                  id="objectives"
                  style={{
                    zIndex: 1000,
                  }}
                />
              )
            }
            {
              tasksEnabled && (
                <Tooltip
                  id="tasks"
                  style={{
                    zIndex: 1000,
                  }}
                />
              )
            }
            <Tooltip
              id="entities"
              style={{
                zIndex: 1000,
              }}
            />
            {
              adminHome && (
                <Tooltip
                  id="admin"
                  style={{
                    zIndex: 1000,
                  }}
                />
              )
            }
            {
              timesheetEnabled && (
                <Tooltip
                  id="timesheet"
                  style={{
                    zIndex: 1000,
                  }}
                />
              )
            }
            <Tooltip
              id="notifications"
              style={{
                zIndex: 1000,
              }}
            />
          </>
        )
      }
      <ProSidebar
        backgroundColor={colors.brand}
        collapsed={!isExpanded}
        collapsedWidth={`${MAIN_SIDEBAR_WIDTH}px`}
        rootStyles={{
          color: 'white',
          height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
          position: 'fixed',
          top: TOP_BAR_HEIGHT,
          zIndex: 100,
          [`.${sidebarClasses.container}`]: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflowY: 'hidden',
          },
        }}
      >
        <Menu
          menuItemStyles={{
            root: {
              backgroundColor: colors.brand,
            },
          }}
          renderExpandIcon={() => undefined}
          rootStyles={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <MenuItem
            component={(
              <div
                onClick={() => setIsExpanded(!isExpanded)}
              />
            )}
          >
            {isExpanded ? (
              <Icon
                name="times"
                size="lg"
              />
            ) : (
              <Icon
                name="bars"
                size="lg"
              />
            )}
          </MenuItem>
          <SubMenu
            label={
              <>
                <UserAvatar
                  alt="User profile"
                  hasProfilePicture={user?.hasProfilePicture}
                  modifiedAt={user?.modifiedAt}
                  size={20}
                  style={{
                    marginRight: 10,
                  }}
                  userId={user?.id}
                />
                <b>{currentOrganization?.name || t('myOrganization')}</b>
              </>
            }
          >
            {
              organizations?.map(organization => (
                <MenuItem
                  active={isSection(links.organization(linkKey(organization)))}
                  component={(
                    <Link
                      to={links.organization(linkKey(organization))}
                    />
                  )}
                  key={organization.id}
                  rootStyles={{
                    backgroundColor: '#977BAE',
                  }}
                >
                  {organization.name}
                </MenuItem>
              ))
            }
          </SubMenu>
          <MenuItem
            active={isSection(links.paths.home)}
            component={(
              <Link
                to={links.paths.home}
              />
            )}
            data-tooltip-content={t('home')}
            data-tooltip-id="home"
          >
            <svg
              aria-hidden="true"
              fill="currentColor"
              focusable="false"
              height="1em"
              style={{
                display: 'inline-block',
                marginRight: 15,
                overflow: 'visible',
                verticalAlign: '-0.125em',
              }}
              viewBox="0 0 576 512"
              width="1.125em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z" />
            </svg>
            {t('home')}
          </MenuItem>
          <MenuItem
            active={isSection(links.paths.projects)}
            component={(
              <Link
                to={links.paths.projects}
              />
            )}
            data-tooltip-content={t('projects')}
            data-tooltip-id="projects"
          >
            <Icon
              name="project-diagram"
              size="1x"
              style={{
                marginRight: 15,
              }}
            />
            {t('projects')}
          </MenuItem>
          {
            objectivesEnabled && (
              <MenuItem
                active={isSection(links.paths.objectives)}
                component={(
                  <Link
                    to={links.paths.objectives}
                  />
                )}
                data-tooltip-content={t('objectives')}
                data-tooltip-id="objectives"
              >
                <Icon
                  name="bullseye"
                  size="1x"
                  style={{
                    marginRight: 15,
                  }}
                />
                {t('objectives')}
              </MenuItem>
            )
          }
          {
            tasksEnabled && (
              <MenuItem
                active={isSection(links.paths.tasks)}
                component={(
                  <Link
                    to={links.paths.tasks}
                  />
                )}
                data-tooltip-content={t('tasks')}
                data-tooltip-id="tasks"
              >
                <Icon
                  name="check-square"
                  size="1x"
                  style={{
                    marginRight: 15,
                  }}
                />
                {t('tasks')}
              </MenuItem>
            )
          }
          <MenuItem
            active={isSection(links.paths.entities)}
            component={(
              <Link
                to={links.paths.entities}
              />
            )}
            data-tooltip-content={t('entities')}
            data-tooltip-id="entities"
          >
            <Icon
              name="shapes"
              size="1x"
              style={{
                marginRight: 15,
              }}
            />
            {t('entities')}
          </MenuItem>
          {
            timesheetEnabled && (
              <MenuItem
                active={isSection(links.timesheet())}
                component={(
                  <Link
                    to={links.timesheet()}
                  />
                )}
                data-tooltip-content={t('timesheet')}
                data-tooltip-id="timesheet"
              >
                <Icon
                  name="clock"
                  size="1x"
                  style={{
                    marginRight: 15,
                  }}
                />
                {t('timesheet')}
              </MenuItem>
            )
          }
          <MenuItem
            active={isSection(links.paths.notifications)}
            component={(
              <Link
                to={links.paths.notifications}
              />
            )}
            data-tooltip-content={t('notifications')}
            data-tooltip-id="notifications"
          >
            <Icon
              name="bell"
              size="1x"
              style={{
                marginRight: 15,
              }}
            />
            {t('notifications')}
            {
              !isExpanded && !!notificationsCount && (
                <>
                  &nbsp;
                  <Dot
                    color={colors.danger}
                    size={10}
                    style={{
                      left: 30,
                      position: 'absolute',
                    }}
                  />
                </>
              )
            }
            {
              isExpanded && !!notificationsCount && (
                <>
                  &nbsp;
                  <NotificationsCounter
                    count={notificationsCount}
                  />
                </>
              )
            }
          </MenuItem>
        </Menu>
        {
          adminHome && (
            <Menu
              menuItemStyles={{
                root: {
                  backgroundColor: colors.brand,
                },
              }}
              rootStyles={{
                borderTop: '1px solid rgba(255, 255, 255, 0.25)',
                flexShrink: 0,
                marginTop: 'auto',
              }}
            >
              <MenuItem
                active={isAdminRoute}
                component={(
                  <Link
                    to={adminHome}
                  />
                )}
                data-tooltip-content={t('admin')}
                data-tooltip-id="admin"
              >
                <AdminIcon
                  style={{
                    marginRight: 15,
                  }}
                />
                {t('admin')}
              </MenuItem>
            </Menu>
          )
        }
      </ProSidebar>
    </ClickOutside>
  );
};

export default Sidebar;
